/* eslint-disable no-restricted-properties */
import { BaseState, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useCurrentWorkspaceId } from './useCurrentWorkspaceId'
import {
  REACT_APP_FACEBOOK_APP_ID,
  REACT_APP_FACEBOOK_CONFIG_ID_V2,
  REACT_APP_FACEBOOK_REDIRECT_URI,
  REACT_APP_INSTAGRAM_REDIRECT_URI,
  REACT_APP_LINKEDIN_CLIENT_ID_V2,
  REACT_APP_LINKEDIN_REDIRECT_URI,
  REACT_APP_MAILCHIMP_CLIENT_ID,
  REACT_APP_MAILCHIMP_REDIRECT_URI,
  REACT_APP_TIKTOK_CLIENT_KEY,
  REACT_APP_TIKTOK_REDIRECT_URI,
  REACT_APP_YOUTUBE_CLIENT_ID,
  REACT_APP_YOUTUBE_REDIRECT_URI,
  REACT_APP_GOOGLE_CLIENT_ID,
  REACT_APP_GOOGLE_REDIRECT_URI,
  REACT_APP_META_ADS_REDIRECT_URI,
  REACT_APP_META_ADS_CONFIG_ID,
  REACT_APP_GO_HIGH_LEVEL_CLIENT_ID,
  REACT_APP_GO_HIGH_LEVEL_REDIRECT_URI,
  REACT_APP_WIX_CLIENT_ID,
  REACT_APP_WIX_REDIRECT_URI,
  REACT_APP_SHOPIFY_CLIENT_ID,
  REACT_APP_SHOPIFY_REDIRECT_URI,
  REACT_APP_GOOGLE_ADS_CLIENT_ID,
  REACT_APP_GOOGLE_ADS_REDIRECT_URI,
} from 'env'
import { XClient } from 'api/clients/XClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { reportError } from 'utils/error'
import { useCallback } from 'react'
import { useFlipperFlag } from './useFlipperFlag'

const generateState = (): string => {
  const stateObject: BaseState = {
    timestamp: new Date().getTime(),
    nonce: crypto.getRandomValues(new Uint32Array(1))[0],
  }
  return window.btoa(JSON.stringify(stateObject))
}

export const decodeState = (state: string): BaseState => {
  return JSON.parse(window.atob(state))
}

const buildUrl = (baseUrl: string, queryParams: Record<string, string>): string => {
  return `${baseUrl}?${new URLSearchParams(queryParams).toString()}`
}

export interface OAuthProviderConfigBase {
  requiresRequestToken?: false
  baseUrl: string | ((shopDomain?: string) => string)
  getQueryParams: (state: string) => Record<string, string>
}

export interface OAuthProviderConfigWithRequestToken {
  requiresRequestToken: true
  requestTokenHandler: (workspaceId: number) => Promise<string>
}

export type OAuthProviderConfig = OAuthProviderConfigBase | OAuthProviderConfigWithRequestToken

export const OAUTH_PROVIDERS: Record<Exclude<ExternalIntegrationEnum, 'word_press'>, OAuthProviderConfig> = {
  [ExternalIntegrationEnum.LinkedIn]: {
    baseUrl: 'https://www.linkedin.com/oauth/v2/authorization',
    getQueryParams: (state) => ({
      client_id: REACT_APP_LINKEDIN_CLIENT_ID_V2,
      redirect_uri: REACT_APP_LINKEDIN_REDIRECT_URI,
      response_type: 'code',
      scope:
        'w_member_social r_basicprofile w_organization_social rw_organization_admin r_organization_social r_organization_followers',
      state,
      prompt: 'login',
    }),
  },
  [ExternalIntegrationEnum.Facebook]: {
    baseUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_FACEBOOK_APP_ID,
      redirect_uri: REACT_APP_FACEBOOK_REDIRECT_URI,
      config_id: REACT_APP_FACEBOOK_CONFIG_ID_V2,
      response_type: 'code granted_scopes',
      override_default_response_type: 'true',
      state,
    }),
  },
  [ExternalIntegrationEnum.Instagram]: {
    baseUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_FACEBOOK_APP_ID,
      redirect_uri: REACT_APP_INSTAGRAM_REDIRECT_URI,
      response_type: 'token granted_scopes',
      display: 'page',
      scope:
        'instagram_basic,instagram_content_publish,pages_show_list,pages_read_engagement,business_management,instagram_manage_insights',
      state,
    }),
  },
  [ExternalIntegrationEnum.TikTok]: {
    baseUrl: 'https://www.tiktok.com/v2/auth/authorize/',
    getQueryParams: (state) => ({
      client_key: REACT_APP_TIKTOK_CLIENT_KEY,
      redirect_uri: REACT_APP_TIKTOK_REDIRECT_URI,
      response_type: 'code',
      scope: 'user.info.basic,video.publish,video.upload,video.list,user.info.profile,user.info.stats',
      state,
    }),
  },
  [ExternalIntegrationEnum.X]: {
    requiresRequestToken: true,
    requestTokenHandler: async (workspaceId) => {
      const res = await XClient.requestToken({ workspaceId })
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        localStorage.setItem('x_oauth_token_secret', res.data.token_secret)
        return `https://api.x.com/oauth/authenticate?oauth_token=${res.data.token}&force_login=true`
      } else {
        showToastMessage('Failed to initiate X OAuth', 'error')
        throw new Error('Failed to get request token')
      }
    },
  },
  [ExternalIntegrationEnum.YouTube]: {
    baseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_YOUTUBE_CLIENT_ID,
      redirect_uri: REACT_APP_YOUTUBE_REDIRECT_URI,
      response_type: 'code',
      access_type: 'offline', // enables refresh token
      include_granted_scopes: 'true', // include all granted scopes in access token
      prompt: 'select_account consent', // force user to choose account, necessary for authentication with multiple accounts
      // https://developers.google.com/identity/protocols/oauth2/scopes#youtube
      scope: 'https://www.googleapis.com/auth/youtube https://www.googleapis.com/auth/youtube.upload',
      state,
    }),
  },
  [ExternalIntegrationEnum.Mailchimp]: {
    baseUrl: 'https://login.mailchimp.com/oauth2/authorize',
    getQueryParams: (state) => ({
      client_id: REACT_APP_MAILCHIMP_CLIENT_ID,
      redirect_uri: REACT_APP_MAILCHIMP_REDIRECT_URI,
      response_type: 'code',
      state,
      prompt: 'login',
    }),
  },
  [ExternalIntegrationEnum.Google]: {
    baseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: REACT_APP_GOOGLE_REDIRECT_URI,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/userinfo.profile',
      state,
      prompt: 'consent',
      access_type: 'offline',
    }),
  },
  [ExternalIntegrationEnum.GoogleDrive]: {
    baseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: REACT_APP_GOOGLE_REDIRECT_URI,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/drive.file',
      state,
      prompt: 'consent',
      access_type: 'offline',
    }),
  },
  [ExternalIntegrationEnum.GoogleBusinessProfile]: {
    baseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: REACT_APP_GOOGLE_REDIRECT_URI,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/business.manage',
      state,
      prompt: 'consent',
      access_type: 'offline',
    }),
  },
  [ExternalIntegrationEnum.MetaAds]: {
    baseUrl: 'https://www.facebook.com/v23.0/dialog/oauth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_FACEBOOK_APP_ID,
      redirect_uri: REACT_APP_META_ADS_REDIRECT_URI,
      config_id: REACT_APP_META_ADS_CONFIG_ID,
      response_type: 'code granted_scopes',
      override_default_response_type: 'true',
      state,
    }),
  },
  [ExternalIntegrationEnum.GoHighLevel]: {
    baseUrl: 'https://marketplace.gohighlevel.com/oauth/chooselocation',
    getQueryParams: (state) => ({
      client_id: REACT_APP_GO_HIGH_LEVEL_CLIENT_ID,
      redirect_uri: REACT_APP_GO_HIGH_LEVEL_REDIRECT_URI,
      response_type: 'code',
      scope:
        'blogs/posts.readonly blogs/list.readonly blogs/post.write blogs/post-update.write blogs/check-slug.readonly blogs/category.readonly blogs/author.readonly contacts.readonly contacts.write users.readonly locations.readonly',
      state,
    }),
  },
  [ExternalIntegrationEnum.Wix]: {
    baseUrl: 'https://www.wix.com/installer/install',
    getQueryParams: (state) => ({
      appId: REACT_APP_WIX_CLIENT_ID,
      redirectUrl: REACT_APP_WIX_REDIRECT_URI,
      state,
    }),
  },
  [ExternalIntegrationEnum.Shopify]: {
    baseUrl: (shopDomain?: string) => {
      if (!shopDomain) {
        throw new Error('Shop domain is required for Shopify OAuth')
      }
      return `https://${shopDomain}/admin/oauth/authorize`
    },
    getQueryParams: (state) => ({
      client_id: REACT_APP_SHOPIFY_CLIENT_ID,
      redirect_uri: REACT_APP_SHOPIFY_REDIRECT_URI,
      scope:
        'read_content,write_content,read_themes,read_analytics,read_orders,read_customers,read_reports,read_products,read_product_listings,read_inventory,read_files',
      state,
    }),
  },
  [ExternalIntegrationEnum.GoogleAds]: {
    baseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    getQueryParams: (state) => ({
      client_id: REACT_APP_GOOGLE_ADS_CLIENT_ID,
      redirect_uri: REACT_APP_GOOGLE_ADS_REDIRECT_URI,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/adwords',
      state,
      prompt: 'consent',
      access_type: 'offline',
    }),
  },
}

const initiateOauthFunction = async (
  provider: ExternalIntegrationEnum,
  accountToRefreshId?: number,
  target?: '_blank',
  options?: string,
  workspaceId?: number,
  shopDomain?: string,
  additionalScopes?: string[],
): Promise<Window | void | null> => {
  const state = generateState()
  const pathWithQueryParams = window.location.pathname + window.location.search

  // Store the pre-OAuth path and accountToRefreshId if provided
  localStorage.setItem('pre_oauth_path', pathWithQueryParams)
  if (accountToRefreshId) {
    localStorage.setItem(`${provider}_accountToRefreshId`, accountToRefreshId.toString())
  } else {
    // clear any previous accountToRefreshId
    Object.keys(localStorage).forEach((key) => {
      if (key.includes('_accountToRefreshId')) {
        localStorage.removeItem(key)
      }
    })
  }

  const providerConfig = OAUTH_PROVIDERS[provider as Exclude<ExternalIntegrationEnum, 'word_press'>]
  if (!providerConfig) {
    throw new Error(`OAuth provider configuration not found for ${provider}`)
  }

  if (providerConfig.requiresRequestToken) {
    // OAuth 1.0a flow for X
    if (!workspaceId) {
      throw new Error('Workspace ID is required for this provider')
    }
    try {
      const redirectUrl = await providerConfig.requestTokenHandler(workspaceId)
      if (target) {
        const windowProxy = window.open(redirectUrl, target, options)
        if (!windowProxy) {
          reportError(new Error('Failed to open window'), {
            severity: 'warning',
            metaData: {
              redirectUrl,
              target,
              options,
            },
          })
        }
        return windowProxy
      } else {
        window.location.href = redirectUrl
      }
    } catch (error) {
      reportError(error)
    }
  } else {
    // Standard OAuth flow
    const queryParams = providerConfig.getQueryParams(state)
    if (additionalScopes?.length && queryParams.scope) {
      const separator = queryParams.scope.includes(',') ? ',' : ' '
      queryParams.scope = queryParams.scope + separator + additionalScopes.join(separator)
    }
    // Handle dynamic baseUrl (for Shopify)
    const baseUrl =
      typeof providerConfig.baseUrl === 'function' ? providerConfig.baseUrl(shopDomain) : providerConfig.baseUrl
    const oauthUrl = buildUrl(baseUrl, queryParams)
    localStorage.setItem(`${provider}_oauth_state`, state)

    if (target) {
      const windowProxy = window.open(oauthUrl, target, options)
      if (!windowProxy) {
        reportError(new Error('Failed to open window'), {
          severity: 'warning',
          metaData: {
            oauthUrl,
            target,
            options,
          },
        })
      }
      return windowProxy
    } else {
      window.location.href = oauthUrl
    }
  }
}

export const useInitiateOauth = () => {
  const workspaceId = useCurrentWorkspaceId()
  const autoRepliesEnabled = useFlipperFlag('auto_replies')
  const initiateOauth = useCallback(
    (
      provider: ExternalIntegrationEnum,
      accountToRefreshId?: number,
      target?: '_blank',
      options?: string,
      shopDomain?: string,
    ) => {
      const additionalScopes: string[] = []
      if (provider === ExternalIntegrationEnum.Instagram && autoRepliesEnabled) {
        additionalScopes.push('instagram_manage_comments', 'pages_manage_metadata')
      }
      return initiateOauthFunction(
        provider,
        accountToRefreshId,
        target,
        options,
        workspaceId,
        shopDomain,
        additionalScopes.length > 0 ? additionalScopes : undefined,
      )
    },
    [workspaceId, autoRepliesEnabled],
  )

  return { initiateOauth }
}
